/**
 * Cross-Validator
 * Compares visa documents against LinkedIn/Twitter data to find inconsistencies
 */

import type {
  Discrepancy,
  CrossValidationResult,
  NormalizedEmployment,
  NormalizedEducation,
  NormalizedSocialMedia,
  CrossValidationConfig,
  TimelineGap,
  OverlappingEmployment,
} from './crossValidationTypes';
import { DEFAULT_VALIDATION_CONFIG } from './crossValidationTypes';
import type { DS160Data, I129Data } from '../parsers/pdf/types';

// Types for LinkedIn/Twitter data (from existing parsers)
interface LinkedInData {
  positions?: Array<{
    companyName?: string;
    title?: string;
    startDate?: { month?: number; year?: number };
    endDate?: { month?: number; year?: number };
  }>;
  education?: Array<{
    schoolName?: string;
    degreeName?: string;
    fieldOfStudy?: string;
    startDate?: { year?: number };
    endDate?: { year?: number };
  }>;
  profile?: {
    twitterHandles?: string[];
  };
}

interface TwitterData {
  username?: string;
  displayName?: string;
}

/**
 * Main cross-validation function
 */
export function crossValidate(
  linkedInData: LinkedInData | null,
  twitterData: TwitterData | null,
  visaDoc: { type: 'DS-160'; data: DS160Data } | { type: 'I-129'; data: I129Data } | null,
  config: CrossValidationConfig = DEFAULT_VALIDATION_CONFIG
): CrossValidationResult {
  const result: CrossValidationResult = {
    totalDiscrepancies: 0,
    criticalCount: 0,
    warningCount: 0,
    infoCount: 0,
    employmentDiscrepancies: [],
    educationDiscrepancies: [],
    socialMediaDiscrepancies: [],
    personalDiscrepancies: [],
    coverageScore: 0,
    linkedInCoverage: 0,
    visaDocCoverage: 0,
    timelineGaps: [],
    overlappingEmployment: [],
    recommendations: [],
    analyzedAt: new Date(),
    documentsCompared: [],
  };

  if (!visaDoc) {
    result.recommendations.push('No visa document uploaded. Upload a DS-160 or I-129 to cross-validate.');
    return result;
  }

  result.documentsCompared.push(visaDoc.type);
  if (linkedInData) result.documentsCompared.push('LinkedIn');
  if (twitterData) result.documentsCompared.push('Twitter');

  // Normalize data for comparison
  const visaEmployment = normalizeVisaEmployment(visaDoc);
  const linkedInEmployment = normalizeLinkedInEmployment(linkedInData);

  const visaEducation = normalizeVisaEducation(visaDoc);
  const linkedInEducation = normalizeLinkedInEducation(linkedInData);

  const visaSocialMedia = normalizeVisaSocialMedia(visaDoc);
  const twitterNormalized = normalizeTwitterData(twitterData);

  // Compare employment
  if (linkedInEmployment.length > 0 || visaEmployment.length > 0) {
    const employmentResult = compareEmployment(
      linkedInEmployment,
      visaEmployment,
      config
    );
    result.employmentDiscrepancies = employmentResult.discrepancies;
    result.timelineGaps = employmentResult.gaps;
    result.overlappingEmployment = employmentResult.overlaps;
    result.linkedInCoverage = employmentResult.linkedInCoverage;
    result.visaDocCoverage = employmentResult.visaDocCoverage;
  }

  // Compare education
  if (linkedInEducation.length > 0 || visaEducation.length > 0) {
    result.educationDiscrepancies = compareEducation(
      linkedInEducation,
      visaEducation,
      config
    );
  }

  // Compare social media
  if (visaSocialMedia.length > 0 || twitterNormalized) {
    result.socialMediaDiscrepancies = compareSocialMedia(
      visaSocialMedia,
      twitterNormalized,
      linkedInData?.profile?.twitterHandles || []
    );
  }

  // Calculate totals
  const allDiscrepancies = [
    ...result.employmentDiscrepancies,
    ...result.educationDiscrepancies,
    ...result.socialMediaDiscrepancies,
    ...result.personalDiscrepancies,
  ];

  result.totalDiscrepancies = allDiscrepancies.length;
  result.criticalCount = allDiscrepancies.filter(d => d.severity === 'critical').length;
  result.warningCount = allDiscrepancies.filter(d => d.severity === 'warning').length;
  result.infoCount = allDiscrepancies.filter(d => d.severity === 'info').length;

  // Calculate overall coverage
  const linkedInItems = linkedInEmployment.length + linkedInEducation.length;
  const visaItems = visaEmployment.length + visaEducation.length;
  const totalItems = linkedInItems + visaItems;
  result.coverageScore = totalItems > 0 ? Math.min(linkedInItems, visaItems) / Math.max(linkedInItems, visaItems) : 0;

  // Generate recommendations
  result.recommendations = generateRecommendations(result);

  return result;
}

/**
 * Normalize visa document employment to common format
 */
function normalizeVisaEmployment(
  visaDoc: { type: 'DS-160'; data: DS160Data } | { type: 'I-129'; data: I129Data }
): NormalizedEmployment[] {
  const employment: NormalizedEmployment[] = [];

  if (visaDoc.type === 'DS-160' && visaDoc.data.employment) {
    for (const job of visaDoc.data.employment) {
      employment.push({
        company: job.employer || '',
        companyNormalized: normalizeCompanyName(job.employer || ''),
        title: job.title || '',
        titleNormalized: normalizeJobTitle(job.title || ''),
        startDate: parseFlexibleDate(job.startDate),
        endDate: job.isCurrent ? null : parseFlexibleDate(job.endDate),
        isCurrent: job.isCurrent || false,
        source: 'visa_doc',
        originalData: job,
      });
    }
  } else if (visaDoc.type === 'I-129') {
    // I-129 has petitioner info (employer) and job info
    const data = visaDoc.data;
    if (data.petitioner?.companyName) {
      employment.push({
        company: data.petitioner.companyName,
        companyNormalized: normalizeCompanyName(data.petitioner.companyName),
        title: data.job?.title || '',
        titleNormalized: normalizeJobTitle(data.job?.title || ''),
        startDate: parseFlexibleDate(data.job?.startDate),
        endDate: parseFlexibleDate(data.job?.endDate),
        isCurrent: true, // I-129 is for current/upcoming employment
        source: 'visa_doc',
        originalData: { petitioner: data.petitioner, job: data.job },
      });
    }
  }

  return employment;
}

/**
 * Normalize LinkedIn employment to common format
 */
function normalizeLinkedInEmployment(linkedIn: LinkedInData | null): NormalizedEmployment[] {
  if (!linkedIn?.positions) return [];

  return linkedIn.positions.map(pos => ({
    company: pos.companyName || '',
    companyNormalized: normalizeCompanyName(pos.companyName || ''),
    title: pos.title || '',
    titleNormalized: normalizeJobTitle(pos.title || ''),
    startDate: pos.startDate ? dateFromYearMonth(pos.startDate.year, pos.startDate.month) : null,
    endDate: pos.endDate ? dateFromYearMonth(pos.endDate.year, pos.endDate.month) : null,
    isCurrent: !pos.endDate,
    source: 'linkedin' as const,
    originalData: pos,
  }));
}

/**
 * Normalize visa document education
 */
function normalizeVisaEducation(
  visaDoc: { type: 'DS-160'; data: DS160Data } | { type: 'I-129'; data: I129Data }
): NormalizedEducation[] {
  if (visaDoc.type !== 'DS-160' || !visaDoc.data.education) return [];

  return visaDoc.data.education.map(edu => ({
    institution: edu.institution || '',
    institutionNormalized: normalizeInstitutionName(edu.institution || ''),
    degree: edu.degree || '',
    fieldOfStudy: edu.fieldOfStudy || '',
    startDate: parseFlexibleDate(edu.startDate),
    endDate: parseFlexibleDate(edu.endDate),
    source: 'visa_doc' as const,
    originalData: edu,
  }));
}

/**
 * Normalize LinkedIn education
 */
function normalizeLinkedInEducation(linkedIn: LinkedInData | null): NormalizedEducation[] {
  if (!linkedIn?.education) return [];

  return linkedIn.education.map(edu => ({
    institution: edu.schoolName || '',
    institutionNormalized: normalizeInstitutionName(edu.schoolName || ''),
    degree: edu.degreeName || '',
    fieldOfStudy: edu.fieldOfStudy || '',
    startDate: edu.startDate?.year ? dateFromYearMonth(edu.startDate.year) : null,
    endDate: edu.endDate?.year ? dateFromYearMonth(edu.endDate.year) : null,
    source: 'linkedin' as const,
    originalData: edu,
  }));
}

/**
 * Normalize visa document social media
 */
function normalizeVisaSocialMedia(
  visaDoc: { type: 'DS-160'; data: DS160Data } | { type: 'I-129'; data: I129Data }
): NormalizedSocialMedia[] {
  if (visaDoc.type !== 'DS-160' || !visaDoc.data.socialMedia) return [];

  return visaDoc.data.socialMedia.map(sm => ({
    platform: sm.platform,
    handle: sm.handle,
    handleNormalized: sm.handle.toLowerCase().replace(/^@/, ''),
    source: 'visa_doc' as const,
  }));
}

/**
 * Normalize Twitter data
 */
function normalizeTwitterData(twitter: TwitterData | null): NormalizedSocialMedia | null {
  if (!twitter?.username) return null;

  return {
    platform: 'Twitter',
    handle: twitter.username,
    handleNormalized: twitter.username.toLowerCase().replace(/^@/, ''),
    source: 'twitter' as const,
  };
}

/**
 * Compare employment records
 */
function compareEmployment(
  linkedIn: NormalizedEmployment[],
  visaDoc: NormalizedEmployment[],
  config: CrossValidationConfig
): {
  discrepancies: Discrepancy[];
  gaps: TimelineGap[];
  overlaps: OverlappingEmployment[];
  linkedInCoverage: number;
  visaDocCoverage: number;
} {
  const discrepancies: Discrepancy[] = [];
  const matchedLinkedIn = new Set<number>();
  const matchedVisaDoc = new Set<number>();

  // Find matching employers
  for (let i = 0; i < visaDoc.length; i++) {
    const visaJob = visaDoc[i];
    let bestMatch: { index: number; score: number } | null = null;

    for (let j = 0; j < linkedIn.length; j++) {
      if (matchedLinkedIn.has(j)) continue;

      const linkedInJob = linkedIn[j];
      const companyScore = stringSimilarity(
        visaJob.companyNormalized,
        linkedInJob.companyNormalized
      );

      if (companyScore >= config.companyMatchThreshold) {
        if (!bestMatch || companyScore > bestMatch.score) {
          bestMatch = { index: j, score: companyScore };
        }
      }
    }

    if (bestMatch) {
      const linkedInJob = linkedIn[bestMatch.index];
      matchedLinkedIn.add(bestMatch.index);
      matchedVisaDoc.add(i);

      // Check for title mismatch
      const titleScore = stringSimilarity(
        visaJob.titleNormalized,
        linkedInJob.titleNormalized
      );
      if (titleScore < config.titleMatchThreshold) {
        discrepancies.push({
          type: 'title_mismatch',
          severity: 'warning',
          field: 'job_title',
          category: 'employment',
          linkedInValue: linkedInJob.title,
          visaDocValue: visaJob.title,
          companyName: visaJob.company,
          similarityScore: titleScore,
          recommendation: `Verify job title for ${visaJob.company}. LinkedIn shows "${linkedInJob.title}" while visa document shows "${visaJob.title}".`,
        });
      }

      // Check for date mismatches
      if (visaJob.startDate && linkedInJob.startDate) {
        const daysDiff = Math.abs(dateDiffDays(visaJob.startDate, linkedInJob.startDate));

        if (daysDiff > config.dateToleranceCritical) {
          discrepancies.push({
            type: 'date_mismatch',
            severity: 'critical',
            field: 'start_date',
            category: 'employment',
            linkedInValue: formatDate(linkedInJob.startDate),
            visaDocValue: formatDate(visaJob.startDate),
            companyName: visaJob.company,
            daysDifference: daysDiff,
            recommendation: `Critical: Start date for ${visaJob.company} differs by ${daysDiff} days. Verify using offer letter or employment records.`,
          });
        } else if (daysDiff > config.dateToleranceWarning) {
          discrepancies.push({
            type: 'date_mismatch',
            severity: 'warning',
            field: 'start_date',
            category: 'employment',
            linkedInValue: formatDate(linkedInJob.startDate),
            visaDocValue: formatDate(visaJob.startDate),
            companyName: visaJob.company,
            daysDifference: daysDiff,
            recommendation: `Start date for ${visaJob.company} differs by ${daysDiff} days. Review and confirm accuracy.`,
          });
        }
      }

      // Check company name variations (usually just info)
      if (visaJob.company !== linkedInJob.company && bestMatch.score < 1) {
        discrepancies.push({
          type: 'company_name_variation',
          severity: 'info',
          field: 'company_name',
          category: 'employment',
          linkedInValue: linkedInJob.company,
          visaDocValue: visaJob.company,
          companyName: visaJob.company,
          similarityScore: bestMatch.score,
          recommendation: `Company name variation detected: "${visaJob.company}" vs "${linkedInJob.company}". Usually acceptable if referring to same entity.`,
        });
      }
    }
  }

  // Flag entries missing from LinkedIn
  if (config.flagMissingInLinkedIn) {
    for (let i = 0; i < visaDoc.length; i++) {
      if (!matchedVisaDoc.has(i)) {
        const job = visaDoc[i];
        discrepancies.push({
          type: 'missing_in_linkedin',
          severity: 'warning',
          field: 'employment',
          category: 'employment',
          visaDocValue: `${job.title} at ${job.company}`,
          companyName: job.company,
          recommendation: `${job.company} appears in visa document but not on LinkedIn. Consider adding to LinkedIn or verify it's intentional.`,
        });
      }
    }
  }

  // Flag entries missing from visa doc
  if (config.flagMissingInVisaDoc) {
    for (let i = 0; i < linkedIn.length; i++) {
      if (!matchedLinkedIn.has(i)) {
        const job = linkedIn[i];
        // Only flag if it's within the relevant timeframe (last 5 years for DS-160)
        const fiveYearsAgo = new Date();
        fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

        if (!job.endDate || job.endDate > fiveYearsAgo) {
          discrepancies.push({
            type: 'missing_in_visa_doc',
            severity: 'critical',
            field: 'employment',
            category: 'employment',
            linkedInValue: `${job.title} at ${job.company}`,
            companyName: job.company,
            recommendation: `${job.company} appears on LinkedIn but not in visa document. This could be flagged during interview. Ensure all employment is disclosed.`,
          });
        }
      }
    }
  }

  // Calculate coverage
  const linkedInCoverage = linkedIn.length > 0
    ? matchedLinkedIn.size / linkedIn.length
    : 1;
  const visaDocCoverage = visaDoc.length > 0
    ? matchedVisaDoc.size / visaDoc.length
    : 1;

  return {
    discrepancies,
    gaps: [], // TODO: Implement timeline gap detection
    overlaps: [], // TODO: Implement overlap detection
    linkedInCoverage,
    visaDocCoverage,
  };
}

/**
 * Compare education records
 */
function compareEducation(
  linkedIn: NormalizedEducation[],
  visaDoc: NormalizedEducation[],
  _config: CrossValidationConfig
): Discrepancy[] {
  const discrepancies: Discrepancy[] = [];

  for (const visaEdu of visaDoc) {
    const match = linkedIn.find(
      li => stringSimilarity(visaEdu.institutionNormalized, li.institutionNormalized) > 0.8
    );

    if (match) {
      // Check degree mismatch
      if (visaEdu.degree && match.degree) {
        const degreeMatch = stringSimilarity(
          visaEdu.degree.toLowerCase(),
          match.degree.toLowerCase()
        );
        if (degreeMatch < 0.7) {
          discrepancies.push({
            type: 'education_mismatch',
            severity: 'warning',
            field: 'degree',
            category: 'education',
            linkedInValue: match.degree,
            visaDocValue: visaEdu.degree,
            institutionName: visaEdu.institution,
            recommendation: `Degree mismatch for ${visaEdu.institution}. Verify correct degree is listed.`,
          });
        }
      }
    } else {
      discrepancies.push({
        type: 'missing_in_linkedin',
        severity: 'info',
        field: 'education',
        category: 'education',
        visaDocValue: visaEdu.institution,
        institutionName: visaEdu.institution,
        recommendation: `${visaEdu.institution} not found on LinkedIn. Consider adding to LinkedIn profile.`,
      });
    }
  }

  return discrepancies;
}

/**
 * Compare social media handles
 */
function compareSocialMedia(
  visaSocialMedia: NormalizedSocialMedia[],
  twitterData: NormalizedSocialMedia | null,
  _linkedInTwitterHandles: string[]
): Discrepancy[] {
  const discrepancies: Discrepancy[] = [];

  // Find Twitter handle in visa doc
  const visaTwitter = visaSocialMedia.find(sm =>
    sm.platform.toLowerCase() === 'twitter' || sm.platform.toLowerCase() === 'x'
  );

  // Compare with actual Twitter
  if (visaTwitter && twitterData) {
    if (visaTwitter.handleNormalized !== twitterData.handleNormalized) {
      discrepancies.push({
        type: 'social_media_mismatch',
        severity: 'critical',
        field: 'twitter_handle',
        category: 'social_media',
        visaDocValue: visaTwitter.handle,
        twitterValue: twitterData.handle,
        platform: 'Twitter',
        recommendation: `Twitter handle mismatch! Visa document shows "@${visaTwitter.handle}" but actual Twitter account is "@${twitterData.handle}". This MUST be corrected before interview.`,
      });
    }
  }

  // Check if Twitter declared in DS-160 but no Twitter profile uploaded
  if (visaTwitter && !twitterData) {
    discrepancies.push({
      type: 'social_media_mismatch',
      severity: 'info',
      field: 'twitter_handle',
      category: 'social_media',
      visaDocValue: visaTwitter.handle,
      platform: 'Twitter',
      recommendation: `Twitter handle "@${visaTwitter.handle}" declared in visa document. Upload Twitter profile to verify accuracy.`,
    });
  }

  return discrepancies;
}

/**
 * Generate overall recommendations
 */
function generateRecommendations(result: CrossValidationResult): string[] {
  const recommendations: string[] = [];

  if (result.criticalCount > 0) {
    recommendations.push(
      `Found ${result.criticalCount} critical issue(s) that should be addressed before your visa interview.`
    );
  }

  if (result.warningCount > 0) {
    recommendations.push(
      `Found ${result.warningCount} warning(s) that you should review and verify.`
    );
  }

  if (result.coverageScore < 0.5) {
    recommendations.push(
      'Low overlap between LinkedIn and visa document. Ensure all employment history is consistently documented.'
    );
  }

  if (result.employmentDiscrepancies.some(d => d.type === 'missing_in_visa_doc')) {
    recommendations.push(
      'Some LinkedIn positions are not in your visa document. Visa officers may ask about undisclosed employment.'
    );
  }

  if (result.socialMediaDiscrepancies.some(d => d.type === 'social_media_mismatch' && d.severity === 'critical')) {
    recommendations.push(
      'Social media handles do not match. This is a common point of scrutiny during interviews.'
    );
  }

  if (recommendations.length === 0) {
    recommendations.push(
      'No major discrepancies found. Your documents appear to be consistent.'
    );
  }

  return recommendations;
}

// Utility functions

function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/,?\s*(inc\.?|llc|ltd\.?|corp\.?|corporation|company|co\.?)$/i, '')
    .replace(/[^\w\s]/g, '')
    .trim();
}

function normalizeJobTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/^(sr\.?|senior|jr\.?|junior|lead|principal|staff|chief)\s+/i, '')
    .replace(/\s+(i|ii|iii|iv|v|1|2|3|4|5)$/i, '')
    .trim();
}

function normalizeInstitutionName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(university|college|institute|school)\b/gi, '')
    .replace(/[^\w\s]/g, '')
    .trim();
}

function stringSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length === 0 || b.length === 0) return 0;

  // Levenshtein distance
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  const maxLen = Math.max(a.length, b.length);
  return 1 - matrix[b.length][a.length] / maxLen;
}

function parseFlexibleDate(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;

  // Try various date formats
  const formats = [
    /(\d{1,2})[\-\/](\d{1,2})[\-\/](\d{4})/, // MM/DD/YYYY or DD/MM/YYYY
    /(\d{1,2})[\-\/]([A-Za-z]{3})[\-\/](\d{4})/, // DD-Mon-YYYY
    /([A-Za-z]+)\s+(\d{4})/, // Month YYYY
    /(\d{4})/, // Just year
  ];

  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      try {
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch {
        continue;
      }
    }
  }

  return null;
}

function dateFromYearMonth(year?: number, month?: number): Date | null {
  if (!year) return null;
  return new Date(year, (month || 1) - 1, 1);
}

function dateDiffDays(a: Date, b: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((a.getTime() - b.getTime()) / msPerDay);
}

function formatDate(date: Date | null): string {
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}
